// src/components/CollapsibleSidebar.jsx
//
// Shared collapse/expand chrome for every sidebar in the app (main nav,
// admin nav, settings tabs, workpaper section list, notes list). Each page
// still owns its own nav content (nav items, tabs, lists) — this component
// only wraps that content with a consistent, single-source-of-truth
// collapse/expand behaviour: a small round tab on the sidebar's edge to
// close it, and a floating round arrow to bring it back when it's closed.
//
// Added Sep 2026 to fix two design issues: (1) the sidebar had no way to
// collapse/expand at all, and (2) each page's own toggle affordance (where
// one existed) didn't match the app's dark/orange palette. This component
// is the single place that behaviour now lives, instead of being
// reimplemented per page (see CLAUDE.md's "four-copy drift" writeup for why
// that pattern is worth avoiding here too).
//
// Usage:
//   <CollapsibleSidebar width="220px" bg={T.s1} borderColor={T.bd}
//     accentFrom={T.or} accentTo={T.or2} position="sticky" top={0} height="100vh">
//     ...existing sidebar JSX (brand, nav, footer) unchanged...
//   </CollapsibleSidebar>
//
// For sidebars that use position:'fixed' with a sibling relying on a fixed
// marginLeft (AdminBoard's <aside>/<main> pair), pass onCollapsedChange so
// the parent can animate that margin in step with the sidebar.
//
// FIX (Sep 2026): the floating reopen arrow was getting visually covered by
// dashboard cards below it once the sidebar collapsed — its `position:fixed`
// containing block/stacking context is technically the viewport, but it
// was still being rendered as a normal DOM child deep inside the page's
// layout tree, and card elements further down that same tree were still
// painting over it in practice. Rendering it through a portal straight
// into document.body removes any ambiguity: it now always paints in its
// own top-level layer, independent of whatever the rest of the page does.
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { T } from '../theme/theme'

const DEFAULT_STORAGE_KEY = 'kailux:sidebarCollapsed'

export default function CollapsibleSidebar({
  width = '220px',
  children,
  bg = T.s2,
  borderColor = T.bd,
  accentFrom = T.or,
  accentTo = T.or2,
  mutedColor = T.lt,
  position = 'relative',
  top = 0,
  height = '100%',
  storageKey = DEFAULT_STORAGE_KEY,
  onCollapsedChange,
  ariaLabel = 'sidebar',
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored === '1') return true
      if (stored === '0') return false
    } catch { /* localStorage unavailable — fall through to default */ }
    return defaultCollapsed
  })

  useEffect(() => {
    try { localStorage.setItem(storageKey, collapsed ? '1' : '0') } catch { /* ignore */ }
    if (onCollapsedChange) onCollapsedChange(collapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed])

  const roundBtnBase = {
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.15s', padding: 0,
  }

  // FIX (Sep 2026): on Workpapers, the sidebar's colored background/border
  // stopped partway down the screen instead of running full height. The
  // cause was stacking `height:'100%'` through several plain (non-flex)
  // block wrappers — a well-known way for nested layouts to silently fall
  // back to content-based height in some browsers. Below, every wrapper
  // that needs to fill its parent's height is itself a flex container, so
  // that height comes from flexbox stretch instead of a percentage chain —
  // only the outermost div still resolves a height/percentage prop, against
  // whatever definite height the calling page's layout provides.
  return (
    <div style={{ position, top, height, flexShrink: 0, display: 'flex' }}>
      {/* Collapsing panel — width animates, fixed-width inner content so
          nothing inside wraps/reflows mid-transition */}
      <div style={{
        width: collapsed ? '0px' : width, overflow: 'hidden',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)', display: 'flex',
      }}>
        <div style={{ width, display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      </div>

      {/* Collapse tab — sits on the sidebar's edge, only shown while open */}
      {!collapsed && (
        <button
          onClick={() => setCollapsed(true)}
          aria-label={`Collapse ${ariaLabel}`}
          title="Collapse sidebar"
          style={{
            ...roundBtnBase,
            position: 'absolute', top: '50%', right: '-13px', transform: 'translateY(-50%)',
            width: '26px', height: '26px',
            border: `1px solid ${borderColor}`, background: bg, color: mutedColor,
            zIndex: 31, boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = accentFrom; e.currentTarget.style.borderColor = accentFrom }}
          onMouseLeave={e => { e.currentTarget.style.color = mutedColor; e.currentTarget.style.borderColor = borderColor }}
        >
          <ChevronLeft size={14} strokeWidth={2.5} />
        </button>
      )}

      {/* Floating reopen arrow — only shown while collapsed. Portaled to
          document.body so it always paints above page content, regardless
          of any ancestor's stacking context. */}
      {collapsed && createPortal(
        <button
          onClick={() => setCollapsed(false)}
          aria-label={`Expand ${ariaLabel}`}
          title="Open sidebar"
          style={{
            ...roundBtnBase,
            position: 'fixed', top: '50%', left: '10px', transform: 'translateY(-50%)',
            width: '32px', height: '32px', border: 'none',
            background: `linear-gradient(135deg,${accentFrom},${accentTo})`, color: T.onpri,
            zIndex: 9999, boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
          }}
        >
          <ChevronRight size={16} strokeWidth={2.5} />
        </button>,
        document.body
      )}
    </div>
  )
}
